import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import { legalDocsApi } from 'api/legalDocs'
import palette from 'constants/palette'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useState } from 'react'
import { emailRegex } from 'utilities/formsyValidators'

const t = buildTranslate('cms.settings.terms_and_policies.suppression_email')

const SuppressionEmailCard: React.FC = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  const { data, isLoading, isError } =
    legalDocsApi.useGetSuppressionNotificationQuery()
  const [updateEmail, { isLoading: isSaving }] =
    legalDocsApi.useUpdateSuppressionNotificationMutation()

  const savedEmail = data?.suppression_notification?.email || ''

  const [isEditing, setIsEditing] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [validationError, setValidationError] = useState('')

  useEffect(() => {
    if (data && !isEditing) {
      setEmailInput(savedEmail)
    }
  }, [data, savedEmail, isEditing])

  const handleEdit = () => {
    setEmailInput(savedEmail)
    setValidationError('')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setEmailInput(savedEmail)
    setValidationError('')
    setIsEditing(false)
  }

  const validate = useCallback((value: string): string => {
    const trimmed = value.trim()
    // Comma, semicolon, and pipe are unambiguous multi-address separators.
    // Whitespace only counts as a separator when it sits BETWEEN two @-bearing
    // segments (e.g. "a@b.com b@c.com"). Internal whitespace in a single
    // malformed email (e.g. "user name@example.com") falls through to the
    // generic "enter a valid email address" message.
    if (/[,;|]/.test(trimmed) || /@.*\s.*@/.test(trimmed)) {
      return t('single_email_validation_error')
    }
    if (trimmed && !emailRegex.test(trimmed)) return t('validation_error')
    return ''
  }, [])

  useEffect(() => {
    if (!isEditing) return
    const timer = setTimeout(() => {
      setValidationError(validate(emailInput))
    }, 500)
    return () => clearTimeout(timer)
  }, [emailInput, isEditing, validate])

  const handleBlur = () => {
    setValidationError(validate(emailInput))
  }

  const handleSave = async () => {
    const trimmed = emailInput.trim()
    const error = validate(emailInput)
    if (error) {
      setValidationError(error)
      return
    }

    try {
      await updateEmail({
        suppression_notification: { email: trimmed },
      }).unwrap()
      setIsEditing(false)
      setValidationError('')
      dispatch(
        alert({
          key: 'success',
          message: trimmed ? t('save_success') : t('clear_success'),
          timeout: 3,
        })
      )
    } catch {
      dispatch(
        alert({
          key: 'danger',
          message: t('save_error'),
          timeout: 3,
        })
      )
    }
  }

  const bgColor = savedEmail ? palette.white : palette.grey10

  if (isLoading || isError) return null

  return (
    <div
      className={`card p-m mb-m ${css({ backgroundColor: bgColor })}`}
      data-testid="suppression-email-card"
    >
      <div className="d-flex align-items-center justify-content-between">
        <h4 className="extra-bold mr-xs">{t('title')}</h4>
        {!isEditing && (
          <Button
            className={css({ height: '32px', fontSize: '14px' })}
            kind={Kind.PRIMARY}
            onClick={handleEdit}
            aria-expanded={false}
          >
            {savedEmail ? t('manage_button') : t('add_button')}
          </Button>
        )}
      </div>

      {!isEditing && (
        <div className="mb-m">
          <Text.BodyText4 color="grey70">
            {savedEmail ? savedEmail : t('empty_description')}
          </Text.BodyText4>
        </div>
      )}

      {isEditing && (
        <div className="mt-s">
          <Text.BodyText4 color="grey70" className="mb-s">
            {t('description')}
          </Text.BodyText4>
          <input
            type="email"
            className={`form-control mb-xs ${css({ maxWidth: '400px' })} ${
              validationError ? 'is-invalid' : ''
            }`}
            value={emailInput}
            onChange={e => {
              const next = e.target.value
              setEmailInput(next)
              // When an error is already showing, recompute synchronously so
              // the message stays accurate as the user keeps editing (clears
              // when fixed, switches when the error type changes). The
              // debounced effect still handles the no-existing-error path.
              if (validationError) setValidationError(validate(next))
            }}
            onBlur={handleBlur}
            placeholder={t('placeholder')}
            aria-label={t('title')}
            aria-invalid={!!validationError}
            aria-describedby="suppression-email-error"
            data-testid="suppression-email-input"
          />
          <div
            id="suppression-email-error"
            className={validationError ? 'mb-xs' : ''}
            role="status"
            aria-live="polite"
          >
            {validationError && (
              <Text.BodyText5 color="cayenne50">
                {validationError}
              </Text.BodyText5>
            )}
          </div>
          <div className="mb-s">
            <Text.BodyText5 color="grey50">{t('hint')}</Text.BodyText5>
          </div>
          <div className="d-flex align-items-center">
            <Button
              kind={Kind.PRIMARY}
              onClick={handleSave}
              disabled={isSaving}
              className="mr-xs"
              data-testid="suppression-email-save"
            >
              {t('save_button')}
            </Button>
            <Button
              kind={Kind.SECONDARY}
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('cancel_button')}
            </Button>
          </div>
          <a
            href={t('help_link_url')}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-s d-inline-block font-size-14"
          >
            {t('help_link_text')}
          </a>
        </div>
      )}
    </div>
  )
}

export default SuppressionEmailCard
